import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { toast as sonnerToast } from 'sonner';

interface ToastProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

/**
 * Thin adapter over sonner's global `toast()` that preserves the
 * `useToast()` hook surface salvaged components consume — i.e.
 * `const { toast } = useToast(); toast({ title, description, variant })`.
 *
 * Variant maps:
 * - `destructive` → `toast.error()` with the title as the headline
 * - default (or omitted) → `toast.success()` (matches the pre-rebuild
 *   semantics; landing/wizard call sites use the destructive path for
 *   failures only, every other site assumes a success-coloured toast)
 *
 * No React state — sonner manages its own queue and the `<Toaster />`
 * component renders it. Mount `<Toaster />` once at the app root
 * (added to `ProtectedLayout` in a follow-up; the dev-shim version
 * is sufficient for Phase 5).
 */
export function useToast() {
  function toast({ title, description, variant }: ToastProps) {
    if (variant === 'destructive') {
      sonnerToast.error(title ?? i18n._(msg`Error`), { description });
    } else {
      sonnerToast.success(title ?? i18n._(msg`Success`), { description });
    }
  }

  return { toast };
}
